import { Loader2 } from "lucide-react";

import { StatusDot, statusDotColorClass } from "@/components/status-dot";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/** Canonical semantic keys mapped to theme `--status-*` tokens. */
export type StatusKey =
  | "pending"
  | "processing"
  | "review"
  | "completed"
  | "error";

export type StatusBadgeSize = "sm" | "md" | "lg";
export type StatusBadgeTone = "default" | "muted";
export type StatusBadgeVisual = "chip" | "dot";

/** Default labels are Title Case (casing policy); pass `label` for domain wording. */
const DEFAULT_LABELS: Record<StatusKey, string> = {
  pending: "Pending",
  processing: "Processing",
  review: "In Review",
  completed: "Completed",
  error: "Failed",
};

const TOKEN_CHIP_STYLES: Record<StatusKey, string> = {
  pending: "bg-status-pending/15 text-status-pending border-status-pending/40",
  processing: "bg-status-processing/15 text-status-processing border-status-processing/40",
  review: "bg-status-review/15 text-status-review border-status-review/40",
  completed: "bg-status-completed/15 text-status-completed border-status-completed/40",
  error: "bg-status-error/15 text-status-error border-status-error/40",
};

/** Mirrors the Badge `size` scale (sm h-5, default h-6, lg h-7). */
const SIZE_CHIP_STYLES: Record<StatusBadgeSize, string> = {
  sm: "h-5 px-1.5 text-[11px] leading-4",
  md: "h-6 px-2 text-xs",
  lg: "h-7 px-2.5 text-sm",
};

const BADGE_SIZE: Record<StatusBadgeSize, "sm" | "default" | "lg"> = {
  sm: "sm",
  md: "default",
  lg: "lg",
};

const SIZE_DOT_STYLES: Record<StatusBadgeSize, string> = {
  sm: "size-1.5",
  md: "size-2",
  lg: "size-2.5",
};

/**
 * Status chip/dot classes for surfaces that cannot render `StatusBadge` (custom triggers, inline
 * health dots). Chips: 1px border, 15% tint, semibold, no forced casing.
 */
export function statusTokenClasses(
  status: StatusKey,
  opts?: {
    tone?: StatusBadgeTone;
    size?: StatusBadgeSize;
    visual?: StatusBadgeVisual;
  }
): string {
  const tone = opts?.tone ?? "default";
  const size = opts?.size ?? "sm";
  const visual = opts?.visual ?? "chip";

  if (visual === "dot") {
    return cn(
      "inline-block shrink-0 rounded-full",
      SIZE_DOT_STYLES[size],
      statusDotColorClass(status, tone)
    );
  }

  return cn(
    "inline-flex items-center gap-1 rounded-md border font-semibold whitespace-nowrap transition-colors",
    tone === "muted"
      ? "border-border bg-muted/50 text-muted-foreground"
      : TOKEN_CHIP_STYLES[status],
    SIZE_CHIP_STYLES[size]
  );
}

export interface StatusBadgeProps {
  /** Canonical semantic key → theme token styles */
  status: StatusKey;
  /** Override display text (Title Case); defaults from status key */
  label?: string;
  /** Defaults to `sm` — the size used in tables, lists, and headers. */
  size?: StatusBadgeSize;
  /** Show spinner; defaults true for pending + processing when unset */
  showSpinner?: boolean;
  /** Chip (default) or color-only dot for selects / inline health */
  visual?: StatusBadgeVisual;
  /** Muted/neutral styling (archived, unknown, not-indexed) */
  tone?: StatusBadgeTone;
  className?: string;
}

export function StatusBadge({
  status,
  label,
  size = "sm",
  showSpinner,
  visual = "chip",
  tone = "default",
  className,
}: StatusBadgeProps) {
  const resolvedLabel = label ?? DEFAULT_LABELS[status];
  const shouldSpin =
    showSpinner ?? (status === "pending" || status === "processing");

  if (visual === "dot") {
    return (
      <StatusDot
        status={status}
        tone={tone}
        label={resolvedLabel}
        className={cn(SIZE_DOT_STYLES[size], className)}
      />
    );
  }

  return (
    <Badge
      variant="outline"
      size={BADGE_SIZE[size]}
      data-status={status}
      className={cn(statusTokenClasses(status, { tone, size, visual: "chip" }), className)}
    >
      {shouldSpin ? (
        <Loader2 className="animate-spin" aria-hidden="true" />
      ) : null}
      {resolvedLabel}
    </Badge>
  );
}
